import type { ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import { getV2HomeForRole, canAccessV2Path } from "./lib/v2Roles";
import { V2Layout } from "./layout/V2Layout";
import { BreadcrumbProvider } from "@/components/layout/BreadcrumbContext";
import { InboxPage } from "./pages/InboxPage";
import { BiensCataloguePage } from "./pages/BiensCataloguePage";
import { BiensVentePage } from "./pages/BiensVentePage";
import { BienVenteCreateFormPage } from "./pages/BienVenteCreateFormPage";
import { BienVenteExportPage } from "./pages/BienVenteExportPage";
import { AppartsForRentPage } from "./pages/AppartsForRentPage";
import { BienCreateFormPage } from "./pages/BienCreateFormPage";
import { CollabPipelinePage } from "./pages/workspaces/CollabPipelinePage";
import { CLevelInsightsPage } from "./pages/workspaces/CLevelInsightsPage";
import { PublicationWorkspacePage } from "./pages/workspaces/PublicationWorkspacePage";
import { PublicationVenteWorkspacePage } from "./pages/workspaces/PublicationVenteWorkspacePage";
import { PrestataireMissionsPage } from "./pages/workspaces/PrestataireMissionsPage";
import { ImmowebSyncPage } from "./pages/ImmowebSyncPage";
// Wrapper V2 qui ajoute fil d'Ariane + padding autour de V1 BienDetailPage.
// V1 BienDetailPage est le formulaire d'édition complet (validation +
// bannières + autosave) — monté dans le layout V2 sans rien dupliquer.
import { V2BienDetailWrapper } from "./pages/V2BienDetailWrapper";
// Import Rentio (xlsx merge) — page admin V1 réutilisée telle quelle, montée
// dans le layout V2 avec son propre padding (le <main> V2 n'en ajoute pas).
import { ImportXlsxPage } from "@/pages/parametres/ImportXlsxPage";
// Pages V1 montées dans le layout V2 (réutilisées telles quelles) — pour que
// TOUT le périmètre admin/V1 soit atteignable depuis la V2 (objectif : plus
// aucune page V1 orpheline une fois la V1 masquée). NB : certains liens
// internes de ces pages pointent encore vers des URL V1 — affinage ultérieur.
import { DashboardPage } from "@/pages/DashboardPage";
import { EquipePage } from "@/pages/parametres/EquipePage";
import { DroitsChampsPage } from "@/pages/parametres/DroitsChampsPage";
import { QuartiersListPage } from "@/pages/parametres/QuartiersListPage";
import { QuartierDetailPage } from "@/pages/parametres/QuartierDetailPage";
import { MetiersPage } from "@/pages/parametres/MetiersPage";
import { TraductionsPage } from "@/pages/parametres/TraductionsPage";
import { AdministrationPage } from "@/pages/parametres/AdministrationPage";
import { InterventionsListPage } from "@/pages/interventions/InterventionsListPage";

// Wrapper léger : monte une page V1 dans le <main> V2 avec le padding standard
// (le <main> V2 n'ajoute pas de padding, contrairement à l'AppLayout V1).
function V1InV2({ children }: { children: ReactNode }) {
  return <div className="p-4 md:p-6">{children}</div>;
}

// Home `/v2` : redirige selon le rôle (getV2HomeForRole) au lieu de forcer
// l'inbox pour tout le monde. Le prestataire atterrit sur "Mes missions"
// (il n'a pas d'inbox), le collaborateur sur le pipeline, etc.
function V2HomeRedirect() {
  const { role } = useRole();
  return <Navigate to={getV2HomeForRole(role)} replace />;
}

// L'inbox n'existe pas pour le prestataire ni le c_level (leur nav ne l'inclut
// pas) : s'ils atterrissent sur /v2/inbox (lien direct, ancien favori, course de
// redirect), on les renvoie sur leur home. (c_level n'est jamais le rôle par défaut
// de chargement — pas de risque de course, contrairement à 'collaborateur'.)
function InboxRoute() {
  const { role } = useRole();
  const { profile, isDevSession } = useAuth();
  // Le rôle n'est fiable qu'une fois le profil chargé : pendant la course de
  // chargement (login réel), RoleContext retombe sur "collaborateur" (qui a accès
  // à l'inbox) → ne RIEN afficher tant que le rôle n'est pas connu, sinon un
  // publication / c_level voit l'inbox flasher avant d'être redirigé.
  const roleReady = isDevSession || !!profile;
  if (!roleReady) return null;
  // L'inbox n'existe que pour admin et collaborateur. Tout autre rôle
  // (publication, c_level, prestataire) → renvoyé sur sa home (jamais l'inbox).
  if (!canAccessV2Path(role, "/v2/inbox")) return <Navigate to={getV2HomeForRole(role)} replace />;
  return <InboxPage />;
}

/**
 * Routes V2 — montées sous /v2/*. Coexistent avec la V1 sans interférer.
 * L'utilisateur peut basculer V1 ↔ V2 à la volée via :
 *  - la sidebar V1 (bouton "Essayer V2")
 *  - la sidebar V2 (bouton "Revenir à la V1")
 *  - les liens "Éditer en V1" dans les sections V2 read-only
 */
export function V2Routes() {
  return (
    <Routes>
      {/* Export PDF de la fiche vente — HORS V2Layout : la vue est un document
          A4 imprimable (window.print()), la sidebar/nav ne doit pas exister
          dans le rendu. Le garde-fou rôle de V2Layout est réappliqué dans la
          page. Le segment statique "export-pdf" prime sur biens/:id/:section. */}
      <Route path="biens/:id/export-pdf" element={<BienVenteExportPage />} />
      <Route
        element={
          <BreadcrumbProvider>
            <V2Layout />
          </BreadcrumbProvider>
        }
      >
        {/* Home — redirige selon le rôle (prestataire → missions, pas d'inbox) */}
        <Route index element={<V2HomeRedirect />} />

        {/* Pages racine */}
        <Route path="inbox" element={<InboxRoute />} />
        <Route path="biens" element={<BiensCataloguePage />} />
        <Route path="biens/new" element={<BienCreateFormPage />} />
        {/* Catalogue VENTE (équipe Sales) — pages distinctes de la location
            (chantier Catalogue Sales 2026-07-12). La fiche d'un bien vente
            reste sous /v2/biens/:id (bifurcation dans V2BienDetailWrapper). */}
        <Route path="biens-vente" element={<BiensVentePage />} />
        <Route path="biens-vente/new" element={<BienVenteCreateFormPage />} />
        <Route path="publication-vente" element={<PublicationVenteWorkspacePage />} />
        <Route path="apparts-for-rent" element={<AppartsForRentPage />} />

        {/* Fiche bien — formulaire d'édition V1 complet (validation +
            bannières champs manquants + autosave) monté dans le layout V2.
            La section éventuelle dans l'URL est passée au composant V1 qui
            sait la gérer (ex. /v2/biens/:id/permissions). */}
        <Route path="biens/:id" element={<V2BienDetailWrapper />} />
        <Route path="biens/:id/:section" element={<V2BienDetailWrapper />} />

        {/* Workspaces métier */}
        <Route path="pipeline" element={<CollabPipelinePage />} />
        <Route path="dashboard" element={<CLevelInsightsPage />} />
        {/* Alias legacy : /v2/insights -> /v2/dashboard (pour ne pas casser
            les anciens bookmarks / liens externes / sidebars en cache). */}
        <Route path="insights" element={<Navigate to="/v2/dashboard" replace />} />
        <Route path="publication" element={<PublicationWorkspacePage />} />
        <Route path="missions" element={<PrestataireMissionsPage />} />
        {/* Détail mission : la complétion ELS/PEB est native dans
            PrestataireMissionsPage (flow guidé). On redirige les anciens liens
            /v2/missions/:type/:id (ex. tâches inbox) vers la liste native. */}
        <Route path="missions/:type/:id" element={<Navigate to="/v2/missions" replace />} />

        {/* Synchronisation Immoweb → biens (C-Level seul, contrôle aussi côté
            Edge Function et RLS). */}
        <Route path="immoweb-sync" element={<ImmowebSyncPage />} />

        {/* Interventions (V1, montée en V2) */}
        <Route path="interventions" element={<V1InV2><InterventionsListPage /></V1InV2>} />

        {/* ── Admin / paramètres (pages V1 montées dans le layout V2) ──────────
            Objectif : tout le périmètre admin est atteignable SANS quitter la
            V2. Le "Dashboard classique" = l'ancien tableau de bord V1 (à
            retravailler en V2 plus tard, mais déjà accessible). */}
        <Route path="admin/dashboard" element={<V1InV2><DashboardPage /></V1InV2>} />
        <Route path="admin/equipe" element={<V1InV2><EquipePage /></V1InV2>} />
        <Route path="admin/droits-champs" element={<V1InV2><DroitsChampsPage fiche="location" /></V1InV2>} />
        <Route path="admin/droits-vente" element={<V1InV2><DroitsChampsPage fiche="vente" /></V1InV2>} />
        <Route path="admin/quartiers" element={<V1InV2><QuartiersListPage /></V1InV2>} />
        <Route path="admin/quartiers/:id" element={<V1InV2><QuartierDetailPage /></V1InV2>} />
        <Route path="admin/metiers" element={<V1InV2><MetiersPage /></V1InV2>} />
        <Route path="admin/traductions" element={<V1InV2><TraductionsPage /></V1InV2>} />
        <Route path="admin/administration" element={<V1InV2><AdministrationPage /></V1InV2>} />
        <Route path="admin/import" element={<V1InV2><ImportXlsxPage /></V1InV2>} />

        {/* Fallback admin inconnu → Équipe (reste dans la V2) */}
        <Route path="admin/*" element={<Navigate to="/v2/admin/equipe" replace />} />
      </Route>
    </Routes>
  );
}
